import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { money, pct, dateShort, marginColor } from "@/lib/format";
import { QUOTE_STATUSES, STATUS_COLORS } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BolViewerDialog from "@/components/workspace/BolViewerDialog";
import DeleteQuoteDialog from "@/components/DeleteQuoteDialog";
import { useAuth } from "@/context/AuthContext";
import {
  Search, History as HistoryIcon, ClipboardList, Pencil, Trash2,
  ChevronLeft, ChevronRight, X, Loader2, AlertTriangle,
} from "lucide-react";

const PAGE_SIZE = 50;
const carrierLabel = (c) => (c || "").replace(/_/g, " ");

function SortableTh({ k, children, right, sortKey, sortDir, onSort }) {
  return (
    <TableHead className={`cursor-pointer select-none ${right ? "text-right" : ""}`} onClick={() => onSort(k)}>
      {children}{sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
    </TableHead>
  );
}

export default function History() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [carrier, setCarrier] = useState("all");
  const [salesRep, setSalesRep] = useState("all");
  const [source, setSource] = useState("all");
  const [bookedOnly, setBookedOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  const [result, setResult] = useState({ items: [], total: 0, pages: 1, page: 1, page_size: PAGE_SIZE });
  const [facets, setFacets] = useState({ reps: [], carriers: [] });
  const [loading, setLoading] = useState(false);

  const [bolQuote, setBolQuote] = useState(null);
  const [delQuote, setDelQuote] = useState(null);

  useEffect(() => {
    api.get("/quotes/history/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, sort: sortKey, dir: sortDir };
      if (search) params.search = search;
      if (status !== "all") params.status = status;
      if (carrier !== "all") params.carrier = carrier;
      if (salesRep !== "all") params.sales_rep = salesRep;
      if (source !== "all") params.source = source;
      if (bookedOnly) params.booked = "1";
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/quotes/history", { params });
      setResult(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, status, carrier, salesRep, source, bookedOnly, dateFrom, dateTo, sortKey, sortDir, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Any filter/sort change jumps back to page 1 (batched with the filter update → single fetch).
  const resetPage = () => setPage(1);
  const onFilter = (setter) => (v) => { setter(v); resetPage(); };

  const changeStatus = async (id, s) => {
    try {
      await api.put(`/quotes/${id}`, { status: s });
      setResult((r) => ({ ...r, items: r.items.map((q) => (q.id === id ? { ...q, status: s } : q)) }));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
    resetPage();
  };

  const th = (k, label, right) => (
    <SortableTh k={k} right={right} sortKey={sortKey} sortDir={sortDir} onSort={setSort}>{label}</SortableTh>
  );

  const items = result.items || [];
  const total = result.total || 0;
  const pages = result.pages || 1;
  const size = result.page_size || PAGE_SIZE;
  const from = total === 0 ? 0 : (result.page - 1) * size + 1;
  const to = Math.min(result.page * size, total);

  const hasFilters = search || status !== "all" || carrier !== "all" || salesRep !== "all"
    || source !== "all" || bookedOnly || dateFrom || dateTo;
  const clearFilters = () => {
    setSearch(""); setStatus("all"); setCarrier("all"); setSalesRep("all");
    setSource("all"); setBookedOnly(false); setDateFrom(""); setDateTo(""); resetPage();
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="sticky top-14 z-20 -mx-4 border-b border-border bg-background/95 px-4 pb-4 pt-1 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tighter">Quote History</h1>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="history-count">
              {total} quote{total === 1 ? "" : "s"} · search, filter &amp; sort
            </p>
          </div>
          {loading && <Loader2 className="mb-1 h-5 w-5 animate-spin text-muted-foreground" data-testid="history-loading" />}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => { setSearch(e.target.value); resetPage(); }}
              placeholder="Search quote #, customer, product, ZIP…" className="h-10 pl-9" data-testid="history-search" />
          </div>
          <Select value={status} onValueChange={onFilter(setStatus)}>
            <SelectTrigger className="h-10 w-36" data-testid="history-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {QUOTE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={carrier} onValueChange={onFilter(setCarrier)}>
            <SelectTrigger className="h-10 w-40" data-testid="history-carrier-filter"><SelectValue placeholder="Carrier" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All carriers</SelectItem>
              {facets.carriers.map((c) => <SelectItem key={c} value={c}>{carrierLabel(c)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={salesRep} onValueChange={onFilter(setSalesRep)}>
            <SelectTrigger className="h-10 w-36" data-testid="history-rep-filter"><SelectValue placeholder="Rep" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All reps</SelectItem>
              {facets.reps.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={onFilter(setSource)}>
            <SelectTrigger className="h-10 w-40" data-testid="history-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="internal">Rep-created</SelectItem>
              <SelectItem value="api">API / WooCommerce</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); resetPage(); }}
              className="h-10 w-[148px]" data-testid="history-date-from" title="Created from" />
            <span className="text-muted-foreground">–</span>
            <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); resetPage(); }}
              className="h-10 w-[148px]" data-testid="history-date-to" title="Created to" />
          </div>
          <Button type="button" variant={bookedOnly ? "default" : "outline"} className="h-10"
            onClick={() => { setBookedOnly((b) => !b); resetPage(); }} data-testid="history-booked-filter">
            Booked only
          </Button>
          {hasFilters && (
            <Button type="button" variant="ghost" className="h-10 gap-1 text-muted-foreground"
              onClick={clearFilters} data-testid="history-clear-filters">
              <X className="h-4 w-4" /> Clear
            </Button>
          )}
        </div>
      </div>

      <div className="border border-border bg-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                {th("created_at", "Date")}
                {th("quote_number", "Quote #")}
                {th("customer_name", "Customer")}
                {th("product_name", "Product")}
                {th("quantity", "Qty", true)}
                <TableHead>Lane</TableHead>
                <TableHead>Carrier</TableHead>
                {th("carrier_cost", "Cost", true)}
                {th("sell_price", "Sell", true)}
                {th("margin_pct", "Margin", true)}
                {th("status", "Status")}
                <TableHead>By</TableHead>
                <TableHead className="text-center">BOL</TableHead>
                <TableHead className="text-center">Edit</TableHead>
                {isAdmin && <TableHead className="text-center">Del</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={isAdmin ? 15 : 14} className="py-12 text-center text-muted-foreground">
                  <HistoryIcon className="mx-auto mb-2 h-7 w-7 opacity-40" />
                  {loading ? "Loading…" : hasFilters ? "No quotes match these filters." : "No quotes yet."}
                </TableCell></TableRow>
              )}
              {items.map((q) => (
                <TableRow
                  key={q.id}
                  className="cursor-pointer border-border hover:bg-secondary/40"
                  data-testid={`history-row-${q.quote_number}`}
                  onClick={() => navigate(`/quotes/${q.id}`)}
                >
                  <TableCell className="text-xs text-muted-foreground">{dateShort(q.created_at)}</TableCell>
                  <TableCell className="metric-num text-sm font-semibold text-[hsl(var(--primary))]">{q.quote_number}</TableCell>
                  <TableCell className="text-sm">{q.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm">
                    {q.product_name}
                    {q.emailed_at && q.last_service_change?.at && q.last_service_change.at > q.emailed_at && (
                      <div
                        className="mt-0.5 inline-flex items-center gap-1 rounded-full border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--warning))]"
                        data-testid="history-changed-after-email"
                        title={`${q.last_service_change.summary} — ${q.last_service_change.user || ""} on ${dateShort(q.last_service_change.at)} (emailed ${dateShort(q.emailed_at)})`}
                      >
                        <AlertTriangle className="h-3 w-3" /> Services changed after emailed
                      </div>
                    )}
                    {q.last_service_change?.summary && (
                      <div className="mt-0.5 truncate text-[11px] text-muted-foreground" data-testid="history-service-note" title={`${q.last_service_change.summary}${q.last_service_change.user ? " — " + q.last_service_change.user : ""}`}>
                        {q.last_service_change.summary}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="metric-num text-right text-sm">{q.quantity}</TableCell>
                  <TableCell className="metric-num text-xs text-muted-foreground">{q.origin_zip || "—"}→{q.destination_zip || "—"}</TableCell>
                  <TableCell className="text-xs">{q.selected_carrier_code?.replace("_", " ") || "—"}</TableCell>
                  <TableCell className="metric-num text-right text-sm">{money(q.carrier_cost)}</TableCell>
                  <TableCell className="metric-num text-right text-sm font-semibold">{money(q.sell_price)}</TableCell>
                  <TableCell className={`metric-num text-right text-sm ${marginColor((q.financial || {}).margin_health)}`}>{pct(q.margin_pct)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select value={q.status} onValueChange={(s) => changeStatus(q.id, s)}>
                      <SelectTrigger className={`h-7 w-28 border text-[11px] font-bold uppercase tracking-wider ${STATUS_COLORS[q.status] || ""}`} data-testid={`status-${q.quote_number}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>{QUOTE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {q.created_by}
                    {q.source === "api" && (
                      <span className="ml-1.5 rounded bg-[hsl(var(--primary))]/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[hsl(var(--primary))]" title={`Created via API by ${q.source_app || "external app"}`} data-testid={`api-badge-${q.quote_number}`}>
                        API
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    {q.bol_pro_number ? (
                      <button
                        onClick={() => setBolQuote(q)}
                        data-testid={`history-bol-${q.quote_number}`}
                        title={`View R+L BOL ${q.bol_pro_number}`}
                        aria-label={`View Bill of Lading ${q.bol_pro_number}`}
                        className="inline-flex items-center gap-1 rounded bg-[hsl(var(--success))]/15 px-1.5 py-1 text-[hsl(var(--success))] transition-colors hover:bg-[hsl(var(--success))]/25"
                      >
                        <ClipboardList className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground/40">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => navigate("/workspace", { state: { editQuoteId: q.id } })}
                      disabled={q.status === "booked" || !!q.bol_pro_number}
                      data-testid={`history-edit-${q.quote_number}`}
                      title={q.bol_pro_number ? "Booked — duplicate to edit" : "Edit quote"}
                      className="inline-flex items-center rounded px-1.5 py-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setDelQuote(q)}
                        data-testid={`history-delete-${q.quote_number}`}
                        title="Delete quote (admin)"
                        className="inline-flex items-center rounded px-1.5 py-1 text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-sm">
          <span className="text-muted-foreground" data-testid="history-range">
            {total === 0 ? "No results" : `Showing ${from}–${to} of ${total}`}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 gap-1" disabled={result.page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="history-prev-page">
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <span className="text-muted-foreground" data-testid="history-page-info">Page {result.page} of {pages}</span>
            <Button variant="outline" size="sm" className="h-8 gap-1" disabled={result.page >= pages || loading}
              onClick={() => setPage((p) => Math.min(pages, p + 1))} data-testid="history-next-page">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <BolViewerDialog open={!!bolQuote} onOpenChange={(v) => { if (!v) setBolQuote(null); }} quote={bolQuote} />
      <DeleteQuoteDialog
        quote={delQuote}
        open={!!delQuote}
        onOpenChange={(v) => { if (!v) setDelQuote(null); }}
        onDeleted={() => load()}
      />
    </div>
  );
}
